import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { TouchTargetProps } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import React from 'react';
import cn from 'classnames';
import { useResponsive } from 'apps/sigma2-ui/src/lib/hooks/useResponsive';
import { isString } from 'lodash';

export type ButtonSize = 'medium' | 'small';

export type ButtonVariant = 'primary' | 'outline' | 'destructive';

export type ButtonProps = Omit<TouchTargetProps, 'content'> & {
  /**
   * @deprecated use children instead
   */
  text?: React.ReactNode;
  children?: React.ReactNode;
  size?: ButtonSize;
  variant?: ButtonVariant;
  innerClassName?: (string | { [key: string]: boolean })[];
} & {
  role?: 'button';
};

const buttonVariants: Record<ButtonVariant, string> = {
  primary:
    'rounded text-blue-100 bg-blue-700 border-transparent border-1 font-bold whitespace-nowrap transition inline-block',
  outline:
    'rounded text-gray-900 bg-transparent border-gray-900 border-1 font-bold whitespace-nowrap transition inline-block',
  destructive:
    'rounded text-red-100 bg-red-700 border-transparent border-1 font-bold whitespace-nowrap transition inline-block',
};

export const Button = (props: ButtonProps) => {
  const resolvedSize = useResponsive(props.size) ?? 'medium';
  const variant = props.variant ?? 'primary';

  return (
    <TouchTarget
      {...props}
      disabled={props.disabled || false}
      className={cn('w-fit', props.className)}
      content={({ hovered, disabled, focused }) => {
        return (
          <div
            className={cn(props.innerClassName ?? buttonVariants[variant], {
              'cursor-pointer': hovered && !disabled,
              'text-sm': resolvedSize === 'medium',
              'px-10': resolvedSize === 'medium',
              'py-3.5': resolvedSize === 'medium',
              'text-xs': resolvedSize === 'small',
              'px-4': resolvedSize === 'small',
              'py-2': resolvedSize === 'small',
              'ring-1 ring-offset-2': focused,
              'opacity-50': disabled,
            })}
          >
            {props.children ?? props.text}
          </div>
        );
      }}
    />
  );
};

export const OutlineButton = ({
  children,
  innerClassName = [],
  ...rest
}: ButtonProps) => {
  return (
    <Button
      variant="outline"
      role="button"
      size="small"
      className="mr-2"
      innerClassName={[
        'rounded text-gray-900 border-1 whitespace-nowrap transition inline-block !ring-0',
        ...(isString(innerClassName) ? [innerClassName] : innerClassName),
      ]}
      {...rest}
    >
      {children}
    </Button>
  );
};
